import React, { useEffect, useState } from 'react';
import {
  Box,
  Card,
  CardContent,
  Typography,
  CircularProgress,
  Grid,
  Paper,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Divider,
  Button,
  Collapse,
  IconButton,
  Tooltip
} from '@mui/material';
import RefreshIcon from '@mui/icons-material/Refresh';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import ExpandLessIcon from '@mui/icons-material/ExpandLess';
import InfoIcon from '@mui/icons-material/Info';
import { useAuth } from '../../contexts/AuthContext';
import { portfolioDataService } from '../../services/portfolioDataService';
import { ExchangeData } from '../../types/portfolio';

interface PortfolioDashboardProps {
  totalPortfolioValue?: number;
}

export const PortfolioDashboard: React.FC<PortfolioDashboardProps> = ({ totalPortfolioValue }) => {
  const { state } = useAuth();
  const [loading, setLoading] = useState(false);
  const [refreshing, setRefreshing] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [accountValue, setAccountValue] = useState<number>(0);
  const [brokerValues, setBrokerValues] = useState<Record<string, number>>({});
  const [exchangePositions, setExchangePositions] = useState<Record<string, any[]>>({});
  
  // Add state variables to control visibility of exchange assets
  const [expandedExchanges, setExpandedExchanges] = useState<Record<string, boolean>>({});
  const [showRawData, setShowRawData] = useState<Record<string, boolean>>({});

  useEffect(() => {
    if (state.user?.id) {
      // Only display the data that was preloaded by the dashboard
      displayPreloadedData();
    }
  }, [state.user]);

  // Display the data that was preloaded by the dashboard
  const displayPreloadedData = async () => {
    try {
      if (!state.user?.id) {
        console.log('No user ID available for displaying portfolio data');
        return;
      }
      
      setLoading(true);
      const portfolioData = await portfolioDataService.getPortfolioData(state.user.id);
      console.log('Portfolio data from service:', JSON.stringify(portfolioData, null, 2));
      
      // Extract values and positions for each exchange
      const values: Record<string, number> = {};
      const positions: Record<string, any[]> = {};
      let initialExpandedState: Record<string, boolean> = {};
      let initialRawDataState: Record<string, boolean> = {};
      
      // Process each exchange
      Object.entries(portfolioData).forEach(([exchange, data]) => {
        console.log(`Processing exchange ${exchange}:`, data);
        
        // Include exchanges that are connected or have positions
        if (data.connected || (data.positions && data.positions.length > 0)) {
          console.log(`Exchange ${exchange} is connected: ${data.connected}, positions: ${data.positions?.length || 0}`);
          values[exchange] = data.totalValue || 0;
          positions[exchange] = data.positions || [];
          initialExpandedState[exchange] = false; // Initially collapsed
          initialRawDataState[exchange] = false; // Initially hide raw data
        } else {
          console.log(`Skipping exchange ${exchange}: not connected or no positions`);
        }
      });
      
      // Update state with the extracted data
      console.log('Setting broker values:', values);
      setBrokerValues(values);
      console.log('Setting exchange positions:', positions);
      setExchangePositions(positions);
      console.log('Setting expanded exchanges:', initialExpandedState);
      setExpandedExchanges(initialExpandedState);
      setShowRawData(initialRawDataState);
      
      // Calculate total account value (this should match the value passed in props)
      const total = Object.values(values).reduce((sum, value) => sum + value, 0);
      console.log('Calculated total account value:', total);
      setAccountValue(total);
      
      console.log('Processed exchange values:', values);
      console.log('Number of exchanges with data:', Object.keys(values).length);
    } catch (error) {
      console.error('Error displaying preloaded portfolio data:', error);
      setError('Failed to display portfolio data');
    } finally {
      setLoading(false);
    }
  };

  // Explicitly refresh all portfolio data
  const refreshPortfolioData = async () => {
    if (!state.user?.id) {
      console.log('No user ID available for refresh');
      return;
    }
    
    try {
      setRefreshing(true);
      setError(null);
      console.log(`Refreshing portfolio data for user: ${state.user.id}`);
      
      // Load all data from the portfolio data service
      await portfolioDataService.loadAllData(state.user.id);
      console.log('Portfolio data refreshed, now displaying updated data');
      
      // Display the newly loaded data
      await displayPreloadedData();
    } catch (error) {
      console.error('Error refreshing portfolio data:', error);
      setError('Failed to refresh portfolio data');
    } finally {
      setRefreshing(false);
    }
  };

  // Toggle the expanded state of an exchange
  const toggleExchangeExpanded = (exchange: string) => {
    setExpandedExchanges(prev => ({
      ...prev,
      [exchange]: !prev[exchange]
    }));
  };

  // Toggle showing raw data for an exchange
  const toggleRawData = (exchange: string) => {
    setShowRawData(prev => ({
      ...prev,
      [exchange]: !prev[exchange]
    }));
  };

  // Format currency values
  const formatCurrency = (value: number) => {
    return new Intl.NumberFormat('en-US', { style: 'currency', currency: 'USD' }).format(value);
  };

  // Render the portfolio summary
  const renderPortfolioSummary = () => {
    return (
      <Card>
        <CardContent>
          <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2 }}>
            <Typography variant="h6">Portfolio Summary</Typography>
            <Button 
              startIcon={<RefreshIcon />} 
              onClick={refreshPortfolioData}
              disabled={refreshing}
              size="small"
              variant="outlined"
            >
              {refreshing ? 'Refreshing...' : 'Refresh'}
            </Button>
          </Box>
          
          <Typography variant="h4" sx={{ mb: 2 }}>
            {formatCurrency(totalPortfolioValue || accountValue || 0)}
          </Typography>
          
          <Divider sx={{ my: 2 }} />
          
          {Object.entries(brokerValues).length > 0 ? (
            <Grid container spacing={2}>
              {Object.entries(brokerValues).map(([exchange, value]) => (
                <Grid item xs={12} key={exchange}>
                  <Paper sx={{ p: 2 }}>
                    <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                      <Typography variant="subtitle1" sx={{ textTransform: 'capitalize' }}>
                        {exchange}
                      </Typography>
                      <Box>
                        <Typography variant="h6" sx={{ display: 'inline-block', mr: 2 }}>
                          {formatCurrency(value)}
                        </Typography>
                        <Button 
                          size="small" 
                          onClick={() => toggleExchangeExpanded(exchange)}
                          endIcon={expandedExchanges[exchange] ? <ExpandLessIcon /> : <ExpandMoreIcon />}
                        >
                          {expandedExchanges[exchange] ? 'Hide' : 'Show'}
                        </Button>
                      </Box>
                    </Box>
                    
                    <Collapse in={expandedExchanges[exchange]}>
                      <Box sx={{ mt: 2 }}>
                        {renderExchangePositions(exchange)}
                        
                        {/* Toggle to show raw data */}
                        <Box sx={{ display: 'flex', justifyContent: 'flex-end', mt: 1 }}>
                          <Button 
                            size="small" 
                            variant="text" 
                            color="info"
                            onClick={() => toggleRawData(exchange)}
                            endIcon={showRawData[exchange] ? <ExpandLessIcon /> : <ExpandMoreIcon />}
                          >
                            {showRawData[exchange] ? 'Hide Raw Data' : 'Show Raw Data'}
                          </Button>
                        </Box>
                        
                        {/* Raw data display */}
                        <Collapse in={showRawData[exchange]}>
                          <Box sx={{ mt: 1, p: 2, bgcolor: '#272822', color: '#f8f8f2', borderRadius: 1, overflow: 'auto', maxHeight: '400px' }}>
                            <Typography variant="caption" sx={{ display: 'block', mb: 1, color: '#a6e22e' }}>
                              Raw API Response:
                            </Typography>
                            <pre style={{ margin: 0, fontFamily: 'monospace', fontSize: '0.75rem' }}>
                              {JSON.stringify(portfolioDataService.getData()[exchange]?.rawData || {}, null, 2)}
                            </pre>
                          </Box>
                        </Collapse>
                      </Box>
                    </Collapse>
                  </Paper>
                </Grid>
              ))}
            </Grid>
          ) : (
            <Typography variant="body1" color="text.secondary" align="center">
              No exchange connections found. Please configure your API keys in the settings.
            </Typography>
          )}
        </CardContent>
      </Card>
    );
  };

  // Render positions for a specific exchange
  const renderExchangePositions = (exchange: string) => {
    const positions = exchangePositions[exchange] || [];
    
    if (positions.length === 0) {
      return (
        <Typography variant="body2" color="text.secondary">
          No positions found for this exchange.
        </Typography>
      );
    }
    
    // Custom rendering based on exchange type
    if (exchange === 'binanceus') {
      return renderBinanceUSPositions(positions);
    }
    
    // Default rendering for other exchanges
    return (
      <TableContainer component={Paper} sx={{ maxHeight: 300 }}>
        <Table size="small" stickyHeader>
          <TableHead>
            <TableRow>
              <TableCell>Asset</TableCell>
              <TableCell align="right">Quantity</TableCell>
              <TableCell align="right">Price</TableCell>
              <TableCell align="right">Value</TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {positions.map((position, index) => {
              // Handle different position formats from different exchanges
              const asset = position.asset || position.symbol || position.currency || '';
              const quantity = position.qty || position.free || position.amount || 0;
              const price = position.price || position.lastPrice || 0;
              const value = position.marketValue || position.usdValue || (quantity * price) || 0;
              
              return (
                <TableRow key={`${exchange}-${asset}-${index}`}>
                  <TableCell component="th" scope="row">
                    {asset}
                  </TableCell>
                  <TableCell align="right">{Number(quantity).toFixed(8)}</TableCell>
                  <TableCell align="right">{formatCurrency(price)}</TableCell>
                  <TableCell align="right">{formatCurrency(value)}</TableCell>
                </TableRow>
              );
            })}
          </TableBody>
        </Table>
      </TableContainer>
    );
  };
  
  // Render Binance US positions specifically
  const renderBinanceUSPositions = (positions: any[]) => {
    return (
      <TableContainer component={Paper} sx={{ maxHeight: 300 }}>
        <Table size="small" stickyHeader>
          <TableHead>
            <TableRow>
              <TableCell>Asset</TableCell>
              <TableCell align="right">Free</TableCell>
              <TableCell align="right">Locked</TableCell>
              <TableCell align="right">Price</TableCell>
              <TableCell align="right">Value (USD)</TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {positions.map((position, index) => {
              const asset = position.asset;
              const free = parseFloat(position.free);
              const locked = parseFloat(position.locked);
              const price = position.price || 0;
              const value = position.marketValue || 0;
              
              return (
                <TableRow key={`binanceus-${asset}-${index}`}>
                  <TableCell component="th" scope="row">
                    {asset}
                  </TableCell>
                  <TableCell align="right">{free.toFixed(8)}</TableCell>
                  <TableCell align="right">{locked.toFixed(8)}</TableCell>
                  <TableCell align="right">{formatCurrency(price)}</TableCell>
                  <TableCell align="right">{formatCurrency(value)}</TableCell>
                </TableRow>
              );
            })}
          </TableBody>
        </Table>
      </TableContainer>
    );
  };

  // Main render
  return (
    <Box>
      {loading ? (
        <Box sx={{ display: 'flex', justifyContent: 'center', p: 3 }}>
          <CircularProgress />
        </Box>
      ) : error ? (
        <Box sx={{ p: 2, bgcolor: 'error.light', borderRadius: 1, mb: 2 }}>
          <Typography color="error">{error}</Typography>
        </Box>
      ) : (
        renderPortfolioSummary()
      )}
    </Box>
  );
};

export default PortfolioDashboard;
